import { useState } from 'react';
import { Link } from 'react-router-dom';

const TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 47,
    annualPrice: 470,
    tagline: 'Essentials',
    color: 'border-white/10',
    accent: 'text-white/70',
    description: 'Research tools and county data to find and acquire tax certificates yourself.',
    perks: [
      'Up to 5 certificate searches/month',
      'County auction calendar access',
      'Basic acquisition alerts',
      'Member dashboard access',
      '5 saved certificate slots',
    ],
  },
  {
    id: 'pro',
    name: 'Professional',
    monthlyPrice: 97,
    annualPrice: 970,
    tagline: 'Most Popular',
    popular: true,
    color: 'border-gold',
    accent: 'text-gold',
    description: 'Full data suite with nationwide coverage and advanced analytics.',
    perks: [
      'Up to 20 certificate searches/month',
      'Nationwide county database',
      'Priority acquisition alerts',
      'Tax records database access',
      'Full backoffice dashboard',
      '50 saved certificate slots',
      'Monthly expert webinars',
    ],
  },
  {
    id: 'elite',
    name: 'Enterprise',
    monthlyPrice: 197,
    annualPrice: 1970,
    tagline: 'White-Glove',
    color: 'border-white/20',
    accent: 'text-white',
    description: 'Unlimited access with a dedicated advisor and priority support.',
    perks: [
      'Unlimited certificate searches',
      'All 50 states + custom criteria',
      '1-on-1 monthly strategy calls',
      'Dedicated research specialist',
      'Private mastermind group',
      'White-glove support team',
      'Unlimited certificate portfolio',
    ],
  },
];

export default function ServiceTiersPreview() {
  const [isAnnual, setIsAnnual] = useState(true);

  return (
    <section className="bg-surface-1 border-y border-white/5 py-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <span className="inline-block px-4 py-1.5 border border-gold/25 text-gold text-xs font-semibold tracking-widest uppercase rounded-full mb-5">
              Service Plans
            </span>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white leading-tight">
              Choose Your<br />Acquisition Service
            </h2>
          </div>
          <p className="text-white/40 text-sm max-w-xs md:text-right leading-relaxed">
            All plans include a 14-day free trial. No credit card required to start.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center gap-1 bg-white/10 border border-white/20 rounded-full p-1">
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                !isAnnual
                  ? 'bg-white/20 text-white'
                  : 'text-white/40 hover:text-white/60'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                isAnnual
                  ? 'bg-gold text-navy-dark'
                  : 'text-white/40 hover:text-white/60'
              }`}
            >
              Annual
              <span
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                  isAnnual ? 'bg-navy-dark text-gold' : 'bg-gold/20 text-gold'
                }`}
              >
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {TIERS.map((tier) => (
            <div
              key={tier.id}
              className={`relative bg-surface-2 border-2 rounded-2xl overflow-hidden transition-all duration-300 ${tier.color} ${
                tier.popular ? 'md:-mt-4' : ''
              }`}
            >
              {tier.popular && (
                <div className="bg-gold text-navy-dark text-xs font-bold text-center py-2.5 tracking-widest uppercase">
                  ★ Most Popular
                </div>
              )}
              <div className="p-7">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-playfair text-2xl font-bold text-white">{tier.name}</h3>
                    {!tier.popular && (
                      <span className="text-xs text-white/30 border border-white/10 px-2 py-0.5 rounded-full">
                        {tier.tagline}
                      </span>
                    )}
                  </div>
                  <p className="text-white/40 text-sm mt-2">{tier.description}</p>
                </div>

                {/* Price */}
                <div className="mb-7">
                  {isAnnual ? (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className={`font-playfair text-5xl font-bold ${tier.accent}`}>
                          ${tier.annualPrice.toLocaleString()}
                        </span>
                        <span className="text-white/30 text-sm">/year</span>
                      </div>
                      <p className="text-green-400/70 text-xs mt-1">
                        Billed annually — saves ${(tier.monthlyPrice * 12 - tier.annualPrice)} vs monthly
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className={`font-playfair text-5xl font-bold ${tier.accent}`}>
                          ${tier.monthlyPrice}
                        </span>
                        <span className="text-white/30 text-sm">/mo</span>
                      </div>
                      <p className="text-amber-400/60 text-xs mt-1">
                        Switch to Annual and save 17%
                      </p>
                    </>
                  )}
                </div>

                <Link
                  to="/pricing"
                  className={`block text-center py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 whitespace-nowrap mb-7 ${
                    tier.popular
                      ? 'bg-gold text-navy-dark hover:bg-gold-light'
                      : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  Start Free Trial
                </Link>

                <ul className="flex flex-col gap-2.5">
                  {tier.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2.5 text-sm text-white/50">
                      <i className="ri-check-line text-gold text-sm flex-shrink-0 mt-0.5" />
                      {perk}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 text-gold text-sm font-semibold hover:text-gold-light transition-colors"
          >
            Compare all plan features <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
